export type TemplateType = {
  id: string
  name: string
  description: string
  json: string
}

// Convert the template JSON into a string for storage
export const prebuiltTemplates: TemplateType[] = [
  {
    id: 'end-of-course-feedback',
    name: 'End-of-Course Feedback Form',
    description: 'A generic feedback form for students to reflect on and evaluate their course experience at the end of a class.',
    json: JSON.stringify({
      title: {
        default: "End-of-Course Feedback"
      },
      description: {
        default: "Please share your honest feedback about this class. Your responses help teachers improve future courses."
      },
      logoPosition: "right",
      pages: [
        {
          name: "feedbackPage",
          elements: [
            {
              type: "rating",
              name: "overallExperience",
              title: { default: "How would you rate your overall experience in this class?" },
              isRequired: true,
              minRateDescription: { default: "Very poor" },
              maxRateDescription: { default: "Excellent" }
            },
            {
              type: "rating",
              name: "learnedAmount",
              title: { default: "How much do you feel you learned in this class?" },
              isRequired: true,
              minRateDescription: { default: "Very little" },
              maxRateDescription: { default: "A lot" }
            },
            {
              type: "rating",
              name: "teacherEffectiveness",
              title: { default: "How effective was the teacher in helping you learn?" },
              isRequired: true,
              minRateDescription: { default: "Not effective" },
              maxRateDescription: { default: "Very effective" }
            },
            {
              type: "comment",
              name: "mostValuable",
              title: { default: "What part of the class was most valuable to you?" }
            },
            {
              type: "comment",
              name: "leastValuable",
              title: { default: "What part of the class was least valuable or could be improved?" }
            },
            {
              type: "comment",
              name: "suggestions",
              title: { default: "Do you have any suggestions to improve this class in the future?" }
            },
            {
              type: "text",
              name: "additionalFeedback",
              title: { default: "Any final comments you'd like to share?" }
            }
          ]
        }
      ]
    })
  },

  {
    id: 'household-income',
    name: 'Household Income Data Form',
    description: 'A template for collecting household income information with dynamic fields based on household size.',
    json: JSON.stringify({
      title: {
        default: 'Household Income Data Form'
      },
      description: {
        default:
          "Thank you for participating in our Household Information Survey. Your input not only helps us serve you better but also plays a crucial role in securing additional funding for your students' schools from the state."
      },
      logoWidth: '700px',
      logoPosition: 'right',
      pages: [
        {
          name: 'page1',
          title: {
            default: 'Household Information'
          },
          elements: [
            {
              type: 'dropdown',
              name: 'question2',
              title: {
                default: 'Snap/TANF Information'
              },
              description: {
                default: 'Do you or anyone in your household receive SNAP or TANF?'
              },
              isRequired: true,
              choices: [
                { value: 'Item 1', text: { default: 'Yes' } },
                { value: 'Item 2', text: { default: 'No' } }
              ]
            },
            {
              type: 'text',
              name: 'question3',
              visibleIf: "{question2} = 'Item 2'",
              title: {
                default: 'Household Size'
              },
              description: {
                default: 'How many people are in your household (including all adults and children)?'
              },
              requiredIf: "{question2} = 'Item 2'",
              inputType: 'number',
              min: 1
            },
            {
              type: 'panel',
              name: 'panel1',
              visibleIf: '{question2} = \'Item 2\' and {question3} >= 1 && {question3} <= 8',
              elements: [
                {
                  type: 'expression',
                  name: 'incomeThreshold',
                  title: {
                    default: 'Income Information'
                  },
                  description: {
                    default: 'Do you make more or less than this amount?'
                  },
                  expression: "iif({question3} = 1, 27861, iif({question3} = 2, 37814, iif({question3} = 3, 47767, iif({question3} = 4, 57720, iif({question3} = 5, 67673, iif({question3} = 6, 77626, iif({question3} = 7, 87579, 97532)))))))",
                  displayStyle: 'currency',
                  includeIntoResult: false
                },
                {
                  type: 'dropdown',
                  name: 'question8',
                  title: {
                    default: 'Is your household income above or below this amount?'
                  },
                  hideNumber: true,
                  choices: [
                    { value: 'Item 1', text: { default: 'More' } },
                    { value: 'Item 2', text: { default: 'Less' } }
                  ]
                }
              ]
            },
            {
              type: 'text',
              name: 'question5',
              visibleIf: '{question2} = \'Item 2\' and {question3} >= 9',
              title: {
                default: 'Income Information'
              },
              description: {
                default: 'Household Income:'
              },
              requiredIf: '{question3} >= 9',
              inputType: 'number'
            },
            {
              type: 'checkbox',
              name: 'question6',
              title: {
                default: 'Verification'
              },
              description: {
                default: 'Please confirm your information is accurate by checking the box below:'
              },
              isRequired: true,
              choices: [
                {
                  value: 'Item 1',
                  text: {
                    default: 'I confirm the information is accurate'
                  }
                }
              ]
            }
          ]
        }
      ],
      widthMode: 'responsive',
      width: '12in'
    })
  }
]

export const defaultSurvey: TemplateType[] = [
  {
    id: 'default',
    name: 'Default Survey Template',
    description: 'A template for teacher feedback survey for end of semester.',
    json: JSON.stringify({
      title: {
        default: 'SurveyName'
      },
      logoPosition: 'right',
      pages: [
        {
          name: 'page1',
          elements: [
            {
              type: 'text',
              name: 'question1',
              title: { default: 'Question' }
            }
          ]
        }
      ]
    })
  }
]
